import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Loader } from "@/components/common/Loader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { MessageSquare, Save, Plus, Trash2, Sparkles } from "lucide-react";

export default function AdminChat() {
  const { refresh } = useSite();
  const [settings, setSettings] = useState(null);
  const [kb, setKb] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingCfg, setSavingCfg] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, k] = await Promise.all([api.get("/admin/settings"), api.get("/admin/chat_kb")]);
      setSettings(s.data);
      setKb(k.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setS = (key, val) => setSettings((p) => ({ ...p, [key]: val }));

  const toggleEnabled = async (val) => {
    setS("chat_enabled", val);
    try {
      await api.put("/admin/settings", { chat_enabled: val });
      toast.success(val ? "Live chat enabled" : "Live chat disabled");
      if (refresh) refresh();
    } catch (e) { toast.error("Failed to update"); setS("chat_enabled", !val); }
  };

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      await api.put("/admin/settings", {
        chat_title: settings.chat_title,
        chat_welcome: settings.chat_welcome,
        chat_system_prompt: settings.chat_system_prompt,
      });
      toast.success("Chat settings saved");
      if (refresh) refresh();
    } catch (e) { toast.error("Save failed"); }
    finally { setSavingCfg(false); }
  };

  const setKbField = (id, field, val) => setKb((list) => list.map((e) => (e.id === id ? { ...e, [field]: val } : e)));

  const saveEntry = async (entry) => {
    try {
      await api.put(`/admin/chat_kb/${entry.id}`, {
        topic: entry.topic, question: entry.question, answer: entry.answer,
        suggested: entry.suggested, order: Number(entry.order) || 0, status: entry.status || "published",
      });
      toast.success("Answer saved");
    } catch (e) { toast.error("Save failed"); }
  };

  const addEntry = async () => {
    try {
      const { data } = await api.post("/admin/chat_kb", {
        topic: "New Topic", question: "New question?", answer: "Answer goes here.",
        suggested: true, order: (kb.length + 1), status: "published",
      });
      setKb((list) => [...list, data]);
      toast.success("Entry added");
    } catch (e) { toast.error("Could not add entry"); }
  };

  const removeEntry = async (id) => {
    try { await api.delete(`/admin/chat_kb/${id}`); setKb((list) => list.filter((e) => e.id !== id)); toast.success("Deleted"); }
    catch (e) { toast.error("Delete failed"); }
  };

  if (loading || !settings) return <AdminLayout><Loader /></AdminLayout>;

  return (
    <AdminLayout>
      <div className="flex items-center gap-3">
        <MessageSquare className="h-7 w-7 text-brand" />
        <div>
          <h1 className="display-title text-4xl">AI Live Chat</h1>
          <p className="mt-1 text-sm text-muted-foreground">Optional assistant that guides visitors. It never replaces support tickets.</p>
        </div>
      </div>

      {/* Enable toggle */}
      <section className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border bg-card p-6">
        <div>
          <p className="font-condensed text-lg font-semibold uppercase tracking-wide">Live Chat Status</p>
          <p className="text-sm text-muted-foreground">{settings.chat_enabled ? "Chat is live on the public site." : "Chat is currently hidden from visitors."}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-sm font-semibold ${settings.chat_enabled ? "text-brand" : "text-muted-foreground"}`}>{settings.chat_enabled ? "On" : "Off"}</span>
          <Switch checked={!!settings.chat_enabled} onCheckedChange={toggleEnabled} data-testid="chat-enabled-toggle" />
        </div>
      </section>

      {/* Config */}
      <section className="mt-6 space-y-4 rounded-xl border border-border bg-card p-6">
        <h2 className="display-title text-2xl">Greeting & Behavior</h2>
        <div><Label>Chat Title</Label><Input value={settings.chat_title || ""} onChange={(e) => setS("chat_title", e.target.value)} data-testid="chat-title-input" className="mt-1.5" /></div>
        <div><Label>Welcome Message</Label><Textarea rows={2} value={settings.chat_welcome || ""} onChange={(e) => setS("chat_welcome", e.target.value)} data-testid="chat-welcome-input" className="mt-1.5" /></div>
        <div>
          <Label>System Prompt (assistant instructions & guardrails)</Label>
          <Textarea rows={8} value={settings.chat_system_prompt || ""} onChange={(e) => setS("chat_system_prompt", e.target.value)} data-testid="chat-system-input" className="mt-1.5 font-mono text-xs" />
          <p className="mt-1 text-xs text-muted-foreground">Defines how the assistant behaves. Keep the rule to route unsure/account questions to support tickets.</p>
        </div>
        <button onClick={saveConfig} disabled={savingCfg} data-testid="chat-config-save" className="inline-flex items-center gap-2 rounded-md bg-brand px-5 py-2.5 text-sm font-semibold uppercase tracking-wide text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors disabled:opacity-60"><Save className="h-4 w-4" /> {savingCfg ? "Saving..." : "Save Settings"}</button>
      </section>

      {/* Knowledge base */}
      <section className="mt-6 rounded-xl border border-border bg-card p-6">
        <div className="flex items-center justify-between">
          <h2 className="display-title text-2xl">Knowledge Base</h2>
          <button onClick={addEntry} data-testid="kb-add" className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-semibold uppercase tracking-wide hover:bg-accent transition-colors"><Plus className="h-4 w-4 text-brand" /> Add Answer</button>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Suggested answers feed the assistant and appear as quick-reply chips when toggled "Suggested".</p>

        <div className="mt-5 space-y-4">
          {kb.map((e) => (
            <div key={e.id} className="rounded-lg border border-border p-4" data-testid={`kb-entry-${e.id}`}>
              <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                <Input value={e.topic || ""} onChange={(ev) => setKbField(e.id, "topic", ev.target.value)} placeholder="Topic" className="font-semibold" data-testid={`kb-topic-${e.id}`} />
                <div className="flex items-center gap-2">
                  <Label className="flex items-center gap-1.5 text-xs"><Sparkles className="h-3.5 w-3.5 text-brand" /> Suggested</Label>
                  <Switch checked={!!e.suggested} onCheckedChange={(v) => setKbField(e.id, "suggested", v)} data-testid={`kb-suggested-${e.id}`} />
                </div>
              </div>
              <Input value={e.question || ""} onChange={(ev) => setKbField(e.id, "question", ev.target.value)} placeholder="Question" className="mt-3" data-testid={`kb-question-${e.id}`} />
              <Textarea rows={3} value={e.answer || ""} onChange={(ev) => setKbField(e.id, "answer", ev.target.value)} placeholder="Answer" className="mt-3" data-testid={`kb-answer-${e.id}`} />
              <div className="mt-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2"><Label className="text-xs">Order</Label><Input type="number" value={e.order ?? 0} onChange={(ev) => setKbField(e.id, "order", ev.target.value)} className="w-20" /></div>
                <div className="flex items-center gap-2">
                  <button onClick={() => saveEntry(e)} data-testid={`kb-save-${e.id}`} className="inline-flex items-center gap-1.5 rounded-md bg-brand px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors"><Save className="h-3.5 w-3.5" /> Save</button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-destructive hover:bg-destructive/10 transition-colors" data-testid={`kb-delete-${e.id}`}><Trash2 className="h-4 w-4" /></button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Delete this answer?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => removeEntry(e.id)} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </AdminLayout>
  );
}
